'use client';

import { ArrowRight, Mail } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import CreatrReview from '@/features/sign-up/components/creatr-review';
import CreatrTeamLetter from '@/features/sign-up/components/creatr-team-letter';
import NewEraSoftware from '@/features/sign-up/components/new-era-software';
import { authClient } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { ANALYTICS_EVENTS, trackEvent } from '@/utils/posthog';

const COMPONENTS = [NewEraSoftware, CreatrReview, CreatrTeamLetter] as const;

function SignUpClient({ variant }: { variant: number }) {
  const Selected = COMPONENTS[variant]!;
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Prefetch the verify page for faster navigation
  useEffect(() => {
    router.prefetch('/verify-account');
  }, [router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Normalize email to lowercase to match better-auth's internal normalization.
    const normalizedEmail = email.trim().toLowerCase();

    try {
      trackEvent(ANALYTICS_EVENTS.signup_email_entered, {
        user_email: normalizedEmail,
      });

      logger.info('Sending OTP', { email: normalizedEmail });

      const otpResult = await authClient.emailOtp.sendVerificationOtp({
        email: normalizedEmail,
        type: 'sign-in',
      });

      if (otpResult.error) {
        setError(
          otpResult.error.message ||
            'Failed to send verification code. Please try again.'
        );
        return;
      }

      const params = new URLSearchParams({ email: normalizedEmail });
      if (returnTo) params.set('returnTo', returnTo);
      const url = `/verify-account?${params.toString()}`;

      if (
        typeof document !== 'undefined' &&
        'startViewTransition' in document
      ) {
        (
          document as Document & {
            startViewTransition: (cb: () => void) => void;
          }
        ).startViewTransition(() => {
          router.push(url);
        });
      } else {
        router.push(url);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      logger.error('Sign up flow failed', {
        email: normalizedEmail,
        error: errorMessage,
      });
      logger.captureException(err as Error, {
        context: { email: normalizedEmail, flow: 'sign-up' },
        tags: { feature: 'auth' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-matte-900 bg-[url('/noise.svg'),url('/Lights.svg')] bg-[size:auto,200%_250%] bg-[position:center,75%_50%]  grid grid-cols-1 xl:grid-cols-[1fr_1.2fr]">
      {/* Left Side - Sign Up Form */}
      <div className="flex-1 flex flex-col justify-between px-4 py-8 pt-12 mx-auto max-w-md 2xl:px-8 2xl:py-12 2xl:pt-20 2xl:max-w-2xl">
        {/* Logo */}
        <div>
          <div className="mb-12 2xl:mb-20">
            <h1 className="text-xl font-bold text-white 2xl:text-2xl">
              Creatr
            </h1>
          </div>
          {/* Main Headline */}
          <div className="mb-12 2xl:mb-16">
            <h2 className="text-3xl font-medium text-white leading-tight mb-4 2xl:text-5xl">
              Build Production Grade Software In Days
            </h2>
            <p className="text-gray-300 text-base 2xl:text-lg">
              Effortlessly create your products with Creatr—no coding or large
              teams needed!
            </p>
          </div>
          {/* Sign Up Form */}
          <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
            <label className="text-sm mb-1 block">Email Address*</label>
            <InputGroup className="bg-transparent mb-3 2xl:mb-4">
              <InputGroupAddon>
                <Mail className="w-4 h-4 text-gray-400" />
              </InputGroupAddon>
              <InputGroupInput
                type="email"
                name="email"
                autoFocus
                placeholder="hello@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </InputGroup>

            {/* Error Message */}
            {error && (
              <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              size={'lg'}
              className="w-full"
              variant={'matte'}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Checking...' : 'Continue'}
              {!isLoading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        </div>

        {/* Terms and Privacy */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            By continuing, you agree to our{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white"
            >
              Terms of Service
            </a>{' '}
            and have read and understood the{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-white"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
      {/* Right Side - Image */}
      <div className={`hidden xl:flex  ${variant === 2 ? 'p-0' : 'p-6'}`}>
        <Selected />
      </div>
    </div>
  );
}

export default SignUpClient;
